import React from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import { Add, Remove, Delete, LocalOffer } from "@mui/icons-material";

const compactChipSx = {
  height: 18,
  fontSize: "0.625rem",
  fontWeight: 600,
  borderRadius: 1,
  "& .MuiChip-label": { px: 0.75 },
};

const CartItemCard = ({ item, onAdd, onRemove, onDelete }) => {
  const theme = useTheme();
  const itemId = item.isOffer ? item.offerId : item.cartItemId || item.id;

  const handleDeleteClick = () => {
    ConfirmDialog({
      type: "warning",
      title: "Remove Item",
      description: `Remove "${item.name}" from the cart?`,
      buttons: [
        { label: "Cancel", style: { color: "#555" } },
        {
          label: "Remove",
          style: { color: "#e53935", fontWeight: 700 },
          onClick: () => onDelete(itemId),
        },
      ],
    });
  };

  const handleRemoveClick = () => {
    if (item.quantity <= 1) {
      ConfirmDialog({
        type: "warning",
        title: "Remove Item",
        description: `This will remove "${item.name}" from the cart. Continue?`,
        buttons: [
          { label: "Cancel", style: { color: "#555" } },
          {
            label: "Remove",
            style: { color: "#e53935", fontWeight: 700 },
            onClick: () => onRemove(itemId),
          },
        ],
      });
    } else {
      onRemove(itemId);
    }
  };

  const itemPrice = item.finalPrice || item.price;
  const unitPrice = itemPrice;
  const totalPrice = itemPrice * item.quantity;

  const modifierChips = [];
  if (item.selectedVariation) {
    modifierChips.push({
      key: "variation",
      label: item.selectedVariation.name,
      sx: {
        ...compactChipSx,
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        color: theme.palette.primary.main,
      },
    });
  }
  if (item.selectedModifiers?.length) {
    item.selectedModifiers.forEach((modifier, index) => {
      modifierChips.push({
        key: `mod-${index}`,
        label: modifier.name,
        sx: {
          ...compactChipSx,
          bgcolor: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.text.secondary,
        },
      });
    });
  }

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.25,
        py: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 1.5,
        bgcolor: "background.paper",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.25),
        },
      }}
    >
      {/* Title row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.5,
          mb: modifierChips.length ? 0.5 : 0.75,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                lineHeight: 1.25,
                fontSize: "0.8125rem",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.name}
            </Typography>
            {item.isOffer && (
              <Chip
                icon={<LocalOffer sx={{ fontSize: 10 }} />}
                label="OFFER"
                size="small"
                sx={{
                  ...compactChipSx,
                  bgcolor: theme.palette.error.main,
                  color: "white",
                  "& .MuiChip-icon": { color: "white", ml: 0.5 },
                }}
              />
            )}
            {item.isExistingItem && (
              <Chip
                label="In Order"
                size="small"
                sx={{
                  ...compactChipSx,
                  bgcolor: alpha("#f59e0b", 0.15),
                  color: "#b45309",
                }}
              />
            )}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={handleDeleteClick}
          sx={{
            color: "error.main",
            bgcolor: alpha(theme.palette.error.main, 0.08),
            width: 26,
            height: 26,
            flexShrink: 0,
            mt: -0.25,
            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.15) },
          }}
        >
          <Delete sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {modifierChips.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.35,
            mb: 0.75,
          }}
        >
          {modifierChips.map((chip) => (
            <Chip key={chip.key} label={chip.label} size="small" sx={chip.sx} />
          ))}
        </Box>
      )}

      {/* Price + quantity */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: theme.palette.primary.main,
              lineHeight: 1.2,
              fontSize: "0.9375rem",
            }}
          >
            Rs. {totalPrice.toFixed(2)}
          </Typography>
          {item.quantity > 1 && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.625rem",
                lineHeight: 1.2,
              }}
            >
              Rs. {unitPrice.toFixed(2)} each
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            bgcolor: alpha(theme.palette.grey[500], 0.06),
            borderRadius: 1.25,
            p: 0.25,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            flexShrink: 0,
          }}
        >
          <IconButton
            size="small"
            onClick={handleRemoveClick}
            sx={{
              width: 24,
              height: 24,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              color: theme.palette.error.main,
              borderRadius: 1,
              "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.15) },
            }}
          >
            <Remove sx={{ fontSize: 14 }} />
          </IconButton>
          <Typography
            sx={{
              minWidth: 22,
              textAlign: "center",
              fontWeight: 800,
              fontSize: "0.8125rem",
            }}
          >
            {item.quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onAdd(itemId)}
            sx={{
              width: 24,
              height: 24,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              color: theme.palette.success.main,
              borderRadius: 1,
              "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.15) },
            }}
          >
            <Add sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default CartItemCard;
